import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Eye, EyeOff, Loader2, Lock } from 'lucide-react'
import { Button, Input, Select, Checkbox } from '@pilot-hub/ui'
import styles from './LoginPage.module.css'

interface LoginPageProps {
  module: 'admin' | 'fuehrung' | 'pilot' | 'leitstelle'
  title: string
  subtitle?: string
  getLoginUsers: () => Promise<Array<{ id: string; benutzername: string; vorname?: string; nachname?: string }>>
  loginEndpoint: string
  redirectPath: string
  onLogin?: (benutzername: string, passwort: string) => Promise<{ success: boolean; error?: string }>
}

/* ── ASB Corporate Colors ──────────────────────────────────────────
   Rot  #C6291A  – Primärfarbe (Buttons, Rahmen, Fokus)
   Gelb #FBE64B  – Akzentfarbe (Glow, Highlights)
   ────────────────────────────────────────────────────────────────── */
const ASB_RED = '#C6291A'
const ASB_RED_LIGHT = '#E5482E'
const ASB_YELLOW = '#FBE64B'

const asbTheme = {
  primary: ASB_RED,
  primaryLight: ASB_RED_LIGHT,
  accent: ASB_YELLOW,
  glow: 'rgba(198, 41, 26, 0.45)',
  glowAccent: 'rgba(251, 230, 75, 0.35)'
}

// Einheitliches ASB-Design für alle Module
const moduleColors: Record<string, typeof asbTheme> = {
  admin: asbTheme,
  fuehrung: asbTheme,
  pilot: asbTheme,
  leitstelle: asbTheme
}

export function LoginPage({
  module,
  title,
  subtitle,
  getLoginUsers,
  loginEndpoint,
  redirectPath,
  onLogin
}: LoginPageProps) {
  const colors = moduleColors[module]

  const [users, setUsers] = useState<Array<{ id: string; benutzername: string; vorname?: string; nachname?: string }>>([])
  const [selectedUser, setSelectedUser] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const usersLoadedRef = useRef(false)

  // Load users ONCE on mount.
  // NOTE: getLoginUsers is an inline arrow function in every module wrapper, so a new
  // reference is created on each render. Using it as an effect dependency caused an
  // infinite render loop (setUsers -> re-render -> new fn -> effect again), which left
  // the submit button permanently disabled. Keep it in a ref and run the effect once.
  const getLoginUsersRef = useRef(getLoginUsers)
  useEffect(() => {
    getLoginUsersRef.current = getLoginUsers
  }, [getLoginUsers])

  useEffect(() => {
    let mounted = true
    const loadUsers = async () => {
      try {
        const userList = await getLoginUsersRef.current()
        if (mounted) {
          setUsers(userList)
          usersLoadedRef.current = true
          setIsLoaded(true)
          // Don't auto-select - show placeholder "Benutzer auswählen"
        }
      } catch (err) {
        if (mounted) {
          console.error('Failed to load users:', err)
          setError('Benutzer konnten nicht geladen werden')
          setIsLoaded(true)
        }
      }
    }
    loadUsers()
    return () => { mounted = false }
  }, [])

  const handleLogin = useCallback(async (e: React.FormEvent) => {
      e.preventDefault()
      if (!selectedUser || !password) return

      setIsLoading(true)
      setError(null)

      try {
        let result: { success: boolean; error?: string }

        if (onLogin) {
          // Use AuthProvider's login function - it sets state directly, no event needed
          result = await onLogin(selectedUser, password)
        } else {
          // Fallback: direct API call (for backward compatibility)
          const apiBase = (window as any).PILOT_HUB_CONFIG?.apiBaseUrl || '/api/v1'
          const response = await fetch(`${apiBase}${loginEndpoint}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                        benutzername: selectedUser,
                        password
                      })
          })

          const data = await response.json()

          if (!response.ok) {
            throw new Error(data.detail || 'Anmeldung fehlgeschlagen')
          }

          // Store auth in the exact shape the AuthProvider + api-client expect.
          const storageKey = (window as any).PILOT_HUB_CONFIG?.authStorageKey || 'pilot-hub-auth'
          const authData = {
            accessToken: data.access_token,
            refreshToken: data.refresh_token || null,
            user: data.user || null,
            state: {
              accessToken: data.access_token,
              access_token: data.access_token,
              refreshToken: data.refresh_token || null,
              refresh_token: data.refresh_token || null,
              user: data.user || null
            }
          }
          localStorage.setItem(storageKey, JSON.stringify(authData))
          window.dispatchEvent(new Event('pilot-hub-auth-changed'))
          result = { success: true }
        }

        if (!result.success) {
          throw new Error(result.error || 'Anmeldung fehlgeschlagen')
        }

        // Redirect using router if available, else fallback to window.location
        const router = (window as any).__PILOT_HUB_ROUTER__
        if (router && router.navigate) {
          router.navigate(redirectPath, { replace: true })
        } else {
          window.location.href = redirectPath
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Anmeldung fehlgeschlagen')
        setIsLoading(false)
      }
    }, [selectedUser, password, rememberMe, loginEndpoint, redirectPath, module, onLogin])

  if (!isLoaded) {
    return (
      <div className={styles.loadingScreen}>
        <div className={styles.bgGradient} />
        <div className={styles.loadingContent}>
          <img src="/logo.png" alt="ASB Drohnenstaffel" className={styles.loadingLogo} />
          <div className={styles.spinner} style={{ borderTopColor: colors.primary, borderRightColor: colors.accent }} />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      {/* Hintergrund: hochwertige, aufloesungsunabhaengige Verlaeufe + sanfte Tiefe.
          Deko-Rechtecke, Raster und Koernung sind entfernt — sie wirkten unscharf
          und „billig". Kein Bitmap => auf jeder Aufloesung scharf. */}
      <div className={styles.bgGradient} />
      <div className={styles.vignette} />

      {/* Login Card - auto-width, centered */}
      <div className={styles.loginCard} style={{
        background: 'rgba(22, 27, 34, 0.95)',
        borderColor: `${colors.primary}55`,
        boxShadow: `0 0 40px ${colors.glow}, 0 0 80px ${colors.glowAccent}, 0 8px 32px rgba(0, 0, 0, 0.5)`
      }}>
        {/* Top accent line */}
        <div className={styles.topAccent} style={{ background: `linear-gradient(90deg, transparent, ${colors.primary}, ${colors.accent}, transparent)` }} />

        <div className={styles.cardContent}>
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.logoWrapper}>
              <img src="/logo.png" alt="ASB Drohnenstaffel Südhessen" className={styles.logo} />
              <div className={styles.pulseRing} style={{ borderColor: colors.accent }} />
            </div>
            <h1 className={styles.title}>{title}</h1>
            {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
          </div>

          {/* Error message */}
          {error && (
            <div className={styles.error}>
              <svg className={styles.errorIcon} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
              {error}
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleLogin} className={styles.form}>
            {/* User Select */}
            <div>
              <label className={styles.label}>Benutzer</label>
              <Select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                disabled={isLoading}
                options={users.map(u => ({
                  // value MUST be the username, not the numeric id: handleLogin sends
                  // `benutzername: selectedUser` to /auth/login, which authenticates by
                  // benutzername. Using u.id produced "Invalid credentials".
                  value: u.benutzername,
                  label: u.benutzername,
                  disabled: false
                }))}
                placeholder="Benutzer auswählen"
                className={styles.select}
                style={{ '--primary-color': colors.primary } as React.CSSProperties}
              />
            </div>

            {/* Password */}
            <div>
              <label className={styles.label}>Passwort</label>
              <div className={styles.passwordWrapper}>
                <Input
                  key={showPassword ? 'text' : 'password'}  // Force remount on type change (fixes React #137)
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  placeholder="••••••••"
                  className={styles.input}
                  style={{ '--primary-color': colors.primary } as React.CSSProperties}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={styles.eyeButton}
                  style={{ '--primary-color': colors.primary } as React.CSSProperties}
                  aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                >
                  {showPassword ? <EyeOff className={styles.eyeIcon} /> : <Eye className={styles.eyeIcon} />}
                </button>
              </div>
            </div>

            {/* Remember Me */}
            <div className={styles.rememberWrapper}>
              <Checkbox
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={isLoading}
                label="Angemeldet bleiben"
                className={styles.checkboxLabel}
                style={{ accentColor: colors.primary }}
              />
            </div>

            {/* Login Button */}
            <Button
              type="submit"
              disabled={isLoading || !selectedUser || !password}
              className={styles.loginButton}
              style={{
                background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryLight})`,
                border: 'none',
                color: '#ffffff'
              }}
            >
              <span className={styles.buttonContent}>
                {isLoading ? <Loader2 className={styles.loader} /> : <Lock className={styles.lockIcon} />}
                {isLoading ? 'Anmeldung...' : 'Anmelden'}
              </span>
              {/* Shine effect */}
              <div className={styles.shine} />
            </Button>
          </form>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <p className={styles.footerText}>
            Pilot-Hub 2 · v1.2.036
          </p>
        </div>
      </div>
    </div>
  )
}
